<template>
  <div class="predictive-search-empty grid">
    <div class="col xs-span l4">
      <p
        v-if="searchQuery"
        class="
          mb-2xl
          text-body-m-regular
          text-body-s-regular-desktop
        "
        v-text="
          $string(
            'predictive_search.no_results',
            { replace: { term: encodeURIComponent(searchQuery) } },
          )
        "
      />

      <div class="predictive-search-empty__blocks">
        <div
          v-if="popularTerms.length"
          class="predictive-search-empty__block"
        >
          <p class="predictive-search-empty__title text-heading-m">
            <strong
              class="text-heading-m"
              v-text="$string('predictive_search.popular_searches')"
            />
          </p>

          <ul class="predictive-search-empty__list">
            <li
              v-for="term of popularTerms.slice(0, 3)"
              :key="term"
            >
              <a
                :href="`${$variable('routes.search')}?q=${term}`"
                class="text-body-m-regular text-body-s-regular-desktop"
                v-text="term"
              />
            </li>
          </ul>
        </div>

        <div
          v-if="isMobile && popularManufacturers.length"
          class="predictive-search-empty__block"
        >
          <p class="predictive-search-empty__title text-heading-m">
            <strong
              class="text-heading-m"
              v-text="$string('predictive_search.popular_manufacturers')"
            />
          </p>

          <ul
            class="
              predictive-search-empty__list
              predictive-search-empty__manufacturers-list
            "
          >
            <li
              v-for="manufacturer of popularManufacturers"
              :key="manufacturer.url"
              class="predictive-search-empty__manufacturers-item"
            >
              <a
                class="text-body-m-regular text-body-s-regular-desktop"
                :href="manufacturer.url"
              >
                <div
                  v-if="manufacturer.logo?.url"
                  class="predictive-search-empty__manufacturers-logo"
                >
                  <responsive-image
                    :image="manufacturer.logo"
                    :ratio="false"
                  />
                </div>

                <span v-text="manufacturer.name" />
              </a>
            </li>

            <li
              v-if="allPopularManufacturers"
              class="predictive-search-empty__manufacturers-item"
            >
              <a
                class="
                  predictive-search-empty__manufacturers-tile
                  text-body-m-regular text-body-s-regular-desktop
                "
                :href="allPopularManufacturers"
              >
                <div class="predictive-search-empty__manufacturers-logo">
                  <icon-grid />
                </div>

                <span
                  v-text="$string('predictive_search.all_popular_manufacturers')"
                />
              </a>
            </li>
          </ul>
        </div>

        <div
          v-if="helpfulLinks.length"
          class="predictive-search-empty__block"
        >
          <p class="predictive-search-empty__title text-heading-m">
            <strong
              class="text-heading-m"
              v-text="$string('predictive_search.helpful_link')"
            />
          </p>

          <ul class="predictive-search-empty__list">
            <li
              v-for="term of helpfulLinks.slice(0, 3)"
              :key="term"
              v-html="
                term.replace(
                  '<a',
                  `<a class='text-body-m-regular text-body-s-regular-desktop'`
                )
              "
            />
          </ul>
        </div>
      </div>
    </div>

    <div
      v-if="!isMobile && popularManufacturers.length"
      class="col xs-span l6-12"
    >
      <p class="predictive-search-empty__title text-heading-m">
        <strong
          class="text-heading-m"
          v-text="$string('predictive_search.popular_manufacturers')"
        />
      </p>

      <ul
        class="
          predictive-search-empty__list
          predictive-search-empty__manufacturers-list
        "
      >
        <li
          v-for="manufacturer of popularManufacturers"
          :key="manufacturer.url"
          class="predictive-search-empty__manufacturers-item"
        >
          <a
            class="text-body-m-regular text-body-s-regular-desktop"
            :href="manufacturer.url"
          >
            <div
              v-if="manufacturer.logo?.url"
              class="predictive-search-empty__manufacturers-logo"
            >
              <responsive-image
                :image="manufacturer.logo"
                :ratio="false"
              />
            </div>

            <span v-text="manufacturer.name" />
          </a>
        </li>

        <li
          v-if="allPopularManufacturers"
          class="predictive-search-empty__manufacturers-item"
        >
          <a
            class="
              predictive-search-empty__manufacturers-tile
              text-body-m-regular text-body-s-regular-desktop
            "
            :href="allPopularManufacturers"
          >
            <div class="predictive-search-empty__manufacturers-logo">
              <icon-grid />
            </div>

            <span
              v-text="$string('predictive_search.all_popular_manufacturers')"
            />
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Predictive search empty (predictive-search-empty)
 * -----------------------------------------------------------------------------
 * Component to display predictive search empty.
 *
 * @param {String} searchQuery - Current search query.
 *
 */
import { mapState } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'

import IconGrid from '~icons/general/grid.svg'

export default {
  name: 'PredictiveSearchEmpty',

  components: {
    IconGrid,
    ResponsiveImage,
  },

  props: {
    searchQuery: {
      type: String,
      default: '',
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns link to all popular manufacturers
     * that are set in theme settings
     * @returns {String}
     */
    allPopularManufacturers() {
      return cnvs.search.allPopularManufacturers
    },

    /**
     * Returns helpful links that are set in theme settings
     * @returns {Array}
     */
    helpfulLinks() {
      return cnvs.search.helpfulLinks
    },

    /**
     * Returns popular manufacturers that are set in theme settings
     * @returns {Array}
     */
    popularManufacturers() {
      if (this.allPopularManufacturers) {
        return cnvs.search.popularManufacturers.slice(0, 9)
      }

      return cnvs.search.popularManufacturers.slice(0, 10)
    },

    /**
     * Returns popular terms that are set in theme settings
     * @returns {Array}
     */
    popularTerms() {
      return cnvs.search.popularTerms
    },
  },
}
</script>
